import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, Lock, UserPlus } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { LOGO } from "@/lib/api";
import RegisterDialog from "@/components/RegisterDialog";

const LANGS = ["tr", "en", "de"];

export const Header = () => {
  const { t, lang, setLang, user, logout } = useApp();
  const [open, setOpen] = useState(false);
  const [regOpen, setRegOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const links = [
    ["/", t("home")], ["/products", t("products")], ["/fairs", t("fairs")],
    ["/news", t("news")], ["/about", t("about")], ["/contact", t("contact")],
  ];

  return (
    <>
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0a0a0a] border-b border-white/10">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-24 md:h-28 flex items-center justify-between gap-6">
          <Link to="/" data-testid="logo-link" className="shrink-0">
            <img src={LOGO} alt="BEDAS" className="h-12 md:h-16 w-auto object-contain logo-light" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7 xl:gap-9 text-[0.86rem] uppercase tracking-[0.16em] text-neutral-300 whitespace-nowrap">
            {links.map(([to, label]) => (
              <Link key={to} to={to} data-testid={`nav-${to === "/" ? "home" : to.slice(1)}`}
                data-active={pathname === to} className="nav-link hover:text-gold">
                {label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <div className="hidden sm:flex items-center gap-1 mr-1">
              {LANGS.map((l) => (
                <button key={l} data-testid={`lang-${l}`} onClick={() => setLang(l)}
                  className={`px-2 py-1 text-[0.8rem] tracking-[0.16em] uppercase transition-colors duration-300 ${
                    lang === l ? "text-gold" : "text-neutral-500 hover:text-neutral-200"}`}>
                  {l}
                </button>
              ))}
            </div>

            <button data-testid="header-register-btn" onClick={() => setRegOpen(true)}
              className="hidden md:inline-flex items-center gap-2 border border-white/20 px-4 py-2.5 text-[0.78rem] uppercase tracking-[0.18em] whitespace-nowrap text-neutral-200 hover:bg-white/10 hover:border-gold/50 transition-colors duration-300">
              <UserPlus size={15} /> {t("register")}
            </button>

            <Link to="/catalog" data-testid="header-catalog-btn"
              className="inline-flex items-center gap-2 bg-gold px-4 py-2.5 text-[0.78rem] uppercase tracking-[0.18em] whitespace-nowrap text-black font-medium hover:bg-white transition-colors duration-300">
              <Lock size={15} /> {t("catalog")}
            </Link>

            {user?.role === "admin" && (
              <button data-testid="header-admin-btn" onClick={() => navigate("/admin")}
                className="hidden md:inline text-[0.8rem] uppercase tracking-[0.2em] text-gold/80 hover:text-gold">
                {t("admin")}
              </button>
            )}
            {user && (
              <button data-testid="header-logout-btn" onClick={logout}
                className="hidden md:inline text-[0.8rem] uppercase tracking-[0.2em] text-neutral-500 hover:text-neutral-200">
                {t("logout")}
              </button>
            )}

            <button data-testid="mobile-menu-btn" className="lg:hidden text-neutral-200"
              onClick={() => setOpen((v) => !v)}>
              {open ? <X size={22} /> : <Menu size={22} />}
            </button>
          </div>
        </div>

        {open && (
          <div data-testid="mobile-menu" className="lg:hidden bg-black/95 border-t border-white/10 px-6 py-8 flex flex-col gap-6">
            {links.map(([to, label]) => (
              <Link key={to} to={to} onClick={() => setOpen(false)}
                data-testid={`mobile-nav-${to === "/" ? "home" : to.slice(1)}`}
                className="text-sm uppercase tracking-[0.2em] text-neutral-300 hover:text-gold">
                {label}
              </Link>
            ))}
            <button data-testid="mobile-register-btn" onClick={() => { setOpen(false); setRegOpen(true); }}
              className="text-left text-sm uppercase tracking-[0.2em] text-gold">{t("register")}</button>
            <div className="flex gap-4 pt-2">
              {LANGS.map((l) => (
                <button key={l} data-testid={`mobile-lang-${l}`} onClick={() => setLang(l)}
                  className={`text-xs uppercase tracking-[0.2em] ${lang === l ? "text-gold" : "text-neutral-500"}`}>{l}</button>
              ))}
            </div>
            {user?.role === "admin" && (
              <Link to="/admin" onClick={() => setOpen(false)} className="text-sm uppercase tracking-[0.2em] text-gold/80">{t("admin")}</Link>
            )}
          </div>
        )}
      </header>
      <RegisterDialog open={regOpen} onOpenChange={setRegOpen} />
    </>
  );
};

export default Header;
